import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./TDSReport.css";

const API = (
  ["localhost", "127.0.0.1"].includes(window.location.hostname)
    ? "http://localhost:8080"
    : process.env.REACT_APP_API_BASE_URL || "https://erp-api.sdcllp.co.in"
).replace(/\/$/, "");

const auth = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  },
});

const number = (value) => {
  const result = Number(value);
  return Number.isFinite(result) ? result : 0;
};

const money = (value) =>
  number(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const dateKey = (value) => {
  if (!value) return "";
  const date = String(value);
  return /^\d{4}-\d{2}-\d{2}/.test(date) ? date.slice(0, 10) : "";
};

const currentFY = () => {
  const today = new Date();
  return today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
};

const downloadCSV = (rows, filename) => {
  const columns = [
    ["Date", "date"],
    ["Company", "company"],
    ["Party", "party"],
    ["Invoice / Bill No.", "documentNo"],
    ["Basic Amount", "amount"],
    ["TDS %", "tdsPercent"],
    ["TDS Amount", "tdsAmount"],
    ["Net Amount", "netAmount"],
    ["Status", "status"],
  ];

  const escapeCSV = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

  const content = [
    columns.map(([label]) => escapeCSV(label)).join(","),
    ...rows.map((row) =>
      columns.map(([, key]) => escapeCSV(row[key])).join(","),
    ),
  ].join("\r\n");

  const blob = new Blob(["\uFEFF", content], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  link.click();

  URL.revokeObjectURL(url);
};

export default function TDSReport({ initialTab = "payable" }) {
  const [tab, setTab] = useState(
    initialTab === "receivable" ? "receivable" : "payable",
  );

  const [entries, setEntries] = useState([]);
  const [company, setCompany] = useState("SDC");
  const [year, setYear] = useState(currentFY());
  const [period, setPeriod] = useState("month");
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [quarter, setQuarter] = useState(
    Math.floor(((new Date().getMonth() + 8) % 12) / 3) + 1,
  );
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await axios.get(`${API}/api/finance-entries`, auth());

      const data = response.data;

      setEntries(
        Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [],
      );
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Unable to load TDS report.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const availableYears = useMemo(() => {
    const years = new Set([currentFY()]);

    entries.forEach((entry) => {
      const date = dateKey(entry.date);
      if (!date) return;

      const y = Number(date.slice(0, 4));
      const m = Number(date.slice(5, 7));

      years.add(m >= 4 ? y : y - 1);
    });

    return [...years].sort((a, b) => b - a);
  }, [entries]);

  const inPeriod = (date) => {
    if (!date) return false;

    if (period === "custom") {
      return (!from || date >= from) && (!to || date <= to);
    }

    const y = Number(date.slice(0, 4));
    const m = Number(date.slice(5, 7));
    const financialYear = m >= 4 ? y : y - 1;

    if (financialYear !== Number(year)) return false;
    if (period === "year") return true;
    if (period === "month") return m === Number(month);

    const entryQuarter = Math.floor(((m + 8) % 12) / 3) + 1;

    return entryQuarter === Number(quarter);
  };

  const rows = useMemo(() => {
    const requiredType = tab === "payable" ? "expense" : "income";
    const query = search.trim().toLowerCase();

    return entries
      .filter((entry) => entry.type === requiredType)
      .filter((entry) => number(entry.tdsAmount) > 0)
      .map((entry) => ({
        id: entry._id,
        date: dateKey(entry.date),
        company: entry.company || "SDC",
        party:
          entry.partyName ||
          entry.projectName ||
          entry.customerName ||
          entry.vendorName ||
          entry.clientName ||
          entry.invoiceDocumentId?.clientName ||
          entry.invoiceDocumentId?.client?.companyName ||
          entry.invoiceDocumentId?.client?.name ||
          "",
        documentNo: entry.invoiceNo || entry.billNo || entry.voucherNo || "",
        amount: number(entry.amount),
        tdsPercent: number(entry.tdsPercent),
        tdsAmount: number(entry.tdsAmount),
        netAmount: number(entry.totalAmount),
        status: entry.locked ? "Locked" : "Unlocked",
      }))
      .filter((row) => row.company === company)
      .filter((row) => inPeriod(row.date))
      .filter(
        (row) =>
          !query ||
          [row.party, row.documentNo, row.date, row.status].some((value) =>
            String(value).toLowerCase().includes(query),
          ),
      )
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [entries, tab, company, year, period, month, quarter, from, to, search]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (result, row) => ({
          amount: result.amount + row.amount,
          tds: result.tds + row.tdsAmount,
          net: result.net + row.netAmount,
        }),
        { amount: 0, tds: 0, net: 0 },
      ),
    [rows],
  );

  const exportReport = () => {
    downloadCSV(rows, `TDS_${tab}_${company}_FY${year}.csv`);
  };

  return (
    <div className={`gst-page tds-report-page tds-report-${tab}`}>
      <div className="gst-heading">
        <div>
          <h2>TDS Reports</h2>
          <p>TDS Payable and Receivable Register</p>
        </div>

        <div className="gst-actions">
          <button onClick={load} disabled={loading}>
            {loading ? "Loading..." : "Refresh"}
          </button>

          <button
            className="gst-primary"
            onClick={exportReport}
            disabled={!rows.length}
          >
            Export CSV
          </button>
        </div>
      </div>

      <div className="gst-filters">
        <div className="tds-tabs" role="group" aria-label="TDS register">
          <button
            type="button"
            className={tab === "payable" ? "active payable" : ""}
            aria-pressed={tab === "payable"}
            onClick={() => setTab("payable")}
          >
            TDS Payable
          </button>
          <button
            type="button"
            className={tab === "receivable" ? "active receivable" : ""}
            aria-pressed={tab === "receivable"}
            onClick={() => setTab("receivable")}
          >
            TDS Receivable
          </button>
        </div>

        <select
          value={company}
          onChange={(event) => setCompany(event.target.value)}
        >
          <option value="SDC">SDC</option>
          <option value="SVB">SVB</option>
          <option value="Magnira">Magnira</option>
        </select>

        <select
          value={year}
          onChange={(event) => setYear(Number(event.target.value))}
        >
          {availableYears.map((fy) => (
            <option key={fy} value={fy}>
              FY {fy}-{String(fy + 1).slice(-2)}
            </option>
          ))}
        </select>

        <select
          value={period}
          onChange={(event) => setPeriod(event.target.value)}
        >
          <option value="month">Monthly</option>
          <option value="quarter">Quarterly</option>
          <option value="year">Financial Year</option>
          <option value="custom">Custom Dates</option>
        </select>

        {period === "month" && (
          <select
            value={month}
            onChange={(event) => setMonth(Number(event.target.value))}
          >
            {[
              "January",
              "February",
              "March",
              "April",
              "May",
              "June",
              "July",
              "August",
              "September",
              "October",
              "November",
              "December",
            ].map((name, index) => (
              <option key={name} value={index + 1}>
                {name}
              </option>
            ))}
          </select>
        )}

        {period === "quarter" && (
          <select
            value={quarter}
            onChange={(event) => setQuarter(Number(event.target.value))}
          >
            <option value={1}>Q1 Apr-Jun</option>
            <option value={2}>Q2 Jul-Sep</option>
            <option value={3}>Q3 Oct-Dec</option>
            <option value={4}>Q4 Jan-Mar</option>
          </select>
        )}

        {period === "custom" && (
          <>
            <input
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
            />

            <input
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
            />
          </>
        )}

        <input
          placeholder="Search party, invoice, bill..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      {error && <div className="gst-error">{error}</div>}

      <div className="gst-summary">
        <div className="gst-stat">
          <span>Entries</span>
          <strong>{rows.length}</strong>
        </div>

        <div className="gst-stat">
          <span>Basic Amount</span>
          <strong>₹ {money(totals.amount)}</strong>
        </div>

        <div className="gst-stat">
          <span>
            {tab === "payable"
              ? "TDS Deducted — Payable Register"
              : "TDS Deducted by Customer — Receivable Register"}
          </span>
          <strong>₹ {money(totals.tds)}</strong>
        </div>

        <div className="gst-stat">
          <span>Recorded Net Amount</span>
          <strong>₹ {money(totals.net)}</strong>
        </div>
      </div>

      <div className="gst-warning">
        This is a deduction register, not a confirmed outstanding balance. TDS
        deposits, Form 26AS/AIS credits and settlement adjustments are not
        tracked in the current Finance Entry model. Verify entries before filing
        or reconciliation.
      </div>

      <div className="gst-table-wrap">
        <table className="gst-table">
          <colgroup>
            <col style={{ width: "45px" }} />
            <col style={{ width: "115px" }} />
            <col style={{ width: "175px" }} />
            <col style={{ width: "165px" }} />
            <col style={{ width: "130px" }} />
            <col style={{ width: "75px" }} />
            <col style={{ width: "125px" }} />
            <col style={{ width: "155px" }} />
            <col style={{ width: "110px" }} />
          </colgroup>
          <thead>
            <tr>
              <th>#</th>
              <th>Date</th>
              <th>Party</th>
              <th>Invoice / Bill No.</th>
              <th>Basic Amount</th>
              <th>TDS %</th>
              <th>TDS Amount</th>
              <th>Recorded Net Amount</th>
              <th>Status</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((row, index) => (
              <tr key={row.id || index}>
                <td>{index + 1}</td>
                <td>{row.date || "—"}</td>
                <td>{row.party || "—"}</td>
                <td>{row.documentNo || "—"}</td>
                <td className="gst-number">{money(row.amount)}</td>
                <td className="gst-number">{money(row.tdsPercent)}</td>
                <td className="gst-number">{money(row.tdsAmount)}</td>
                <td className="gst-number">{money(row.netAmount)}</td>
                <td>
                  <span
                    className={`tds-lock-badge ${row.status === "Locked" ? "locked" : "unlocked"}`}
                  >
                    {row.status}
                  </span>
                </td>
              </tr>
            ))}

            {!rows.length && (
              <tr>
                <td colSpan={9} className="gst-empty">
                  {loading ? "Loading report..." : "No TDS entries found"}
                </td>
              </tr>
            )}
          </tbody>

          {!!rows.length && (
            <tfoot>
              <tr>
                <td colSpan={4}>TOTAL</td>
                <td className="gst-number">{money(totals.amount)}</td>
                <td>—</td>
                <td className="gst-number">{money(totals.tds)}</td>
                <td className="gst-number">{money(totals.net)}</td>
                <td>—</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <div className="gst-footer">
        Showing {rows.length} records • {company} • FY {year}-
        {String(year + 1).slice(-2)}
      </div>
    </div>
  );
}
